import { useState } from "react";
import { useSelector } from "react-redux";
import { Dropdown, Table } from "semantic-ui-react";
import { toast } from "../..";
import { UserRole } from "../../actions/authentificationActions";
import { ISRB } from "../../models/srb";
import { AppState } from "../../store/configureStore";
import { downloadSRB } from "../../api/serviceRecordBooks";
import { useIntl } from "react-intl";
import { formatDate } from "../../models/common";
import { SRBFormModal } from "./SRBFormModal";
import { ICrewMember } from "../../models/crewMember";
import { RequestFeedbackIndicator } from "../../components/RequestFeedbackIndicator/RequestFeedbackIndicator";

type Props = {
    srb: ISRB;
    crewMember: ICrewMember;
    fetchCrewMember: () => Promise<void>;
};

export const SRBRow = ({ srb, crewMember, fetchCrewMember }: Props) => {
    const user = useSelector((state: AppState) => state.user);
    const [loading, setLoading] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const intl = useIntl();
    const disabled = srb.guid.includes("ECDB_SRB");

    const handleDownloadSRBOrCombinedDocument = async (isCombined: boolean) => {
        setLoading(true);
        let filename = srb.serialNumber + ".pdf";
        if (isCombined) filename = "CD_" + filename;
        try {
            await downloadSRB(srb.guid, isCombined, filename);
        } catch {
            toast(intl.formatMessage({ id: "failedToDownloadSRB" }), false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Table.Row>
            <Table.Cell>{srb.serialNumber}</Table.Cell>
            <Table.Cell>{formatDate(srb.issueDate)}</Table.Cell>
            <Table.Cell>{srb.active ? "Active" : "Inactive"}</Table.Cell>
            <Table.Cell collapsing>
                <span className="buttons-container">
                    <Dropdown
                        id="srbs-options"
                        floating
                        text={intl.formatMessage({ id: "options" })}
                        loading={loading}
                    >
                        <Dropdown.Menu>
                            <Dropdown.Item
                                text={intl.formatMessage({ id: "viewEditDetails" })}
                                onClick={() => setOpenEditModal(true)}
                                disabled={loading || disabled}
                            />
                            {user.role !== UserRole.EnforcementAuthorityUser && (
                                <>
                                    <Dropdown.Item
                                        text={intl.formatMessage({ id: "downloadAsSRB" })}
                                        onClick={() => handleDownloadSRBOrCombinedDocument(false)}
                                        disabled={loading || disabled}
                                    />
                                    <Dropdown.Item
                                        text={intl.formatMessage({ id: "downloadAsComb" })}
                                        onClick={() => handleDownloadSRBOrCombinedDocument(true)}
                                        disabled={loading || disabled}
                                    />
                                </>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                    {srb.requestFeedbacks.length > 0 && (
                        <RequestFeedbackIndicator feedbacks={srb.requestFeedbacks} />
                    )}
                </span>
            </Table.Cell>
            <SRBFormModal
                open={openEditModal}
                close={() => setOpenEditModal(false)}
                fetchCrewMember={fetchCrewMember}
                crewMember={crewMember}
                srb={srb}
            />
        </Table.Row>
    );
};
